import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { PropertyKitTemplate, PropertyKitTemplateItem } from './types';

const QUERY_KEY = ['property-kit-templates'];

export function usePropertyKitTemplates() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all templates
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<PropertyKitTemplate[]> => {
      const { data, error } = await supabase
        .from('property_kit_templates')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      return data as PropertyKitTemplate[];
    },
  });

  // Create template
  const createMutation = useMutation({
    mutationFn: async (input: { name: string; description?: string }) => {
      const { data, error } = await supabase
        .from('property_kit_templates')
        .insert({
          name: input.name,
          description: input.description,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Plantilla creada',
        description: 'La plantilla se ha creado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo crear la plantilla: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Update template
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: { id: string; name: string; description?: string }) => {
      const { data, error } = await supabase
        .from('property_kit_templates')
        .update({
          name: input.name,
          description: input.description,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Plantilla actualizada',
        description: 'La plantilla se ha actualizado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo actualizar la plantilla: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Delete template
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('property_kit_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Plantilla eliminada',
        description: 'La plantilla se ha eliminado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo eliminar la plantilla: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  return {
    templates: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createTemplate: createMutation.mutateAsync,
    updateTemplate: updateMutation.mutateAsync,
    deleteTemplate: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

// Hook for template items
export function usePropertyKitTemplateItems(templateId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['property-kit-template-items', templateId],
    enabled: !!templateId,
    queryFn: async (): Promise<PropertyKitTemplateItem[]> => {
      const { data, error } = await supabase
        .from('property_kit_template_items')
        .select(`
          *,
          item:property_kit_items(
            *,
            category:property_kit_categories(*)
          )
        `)
        .eq('template_id', templateId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as PropertyKitTemplateItem[];
    },
  });

  // Add item to template
  const addItemMutation = useMutation({
    mutationFn: async ({ itemId, defaultQuantity }: { itemId: string; defaultQuantity?: number }) => {
      const { data, error } = await supabase
        .from('property_kit_template_items')
        .insert({
          template_id: templateId,
          item_id: itemId,
          default_quantity: defaultQuantity,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-kit-template-items', templateId] });
      toast({
        title: 'Artículo agregado',
        description: 'El artículo se ha agregado a la plantilla.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo agregar el artículo: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Remove item from template
  const removeItemMutation = useMutation({
    mutationFn: async (templateItemId: string) => {
      const { error } = await supabase
        .from('property_kit_template_items')
        .delete()
        .eq('id', templateItemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-kit-template-items', templateId] });
      toast({
        title: 'Artículo eliminado',
        description: 'El artículo se ha eliminado de la plantilla.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo eliminar el artículo: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  return {
    templateItems: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    addItem: addItemMutation.mutateAsync,
    removeItem: removeItemMutation.mutateAsync,
    isAdding: addItemMutation.isPending,
    isRemoving: removeItemMutation.isPending,
  };
}
